import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  VitaConversionPlan,
  VitaConversionResult,
  VitaWalletService,
} from '../../../services/vita-wallet.service';

export interface ConvertCurrencyData {
  /** UUID de la wallet master (informativo; el exchange opera sobre el negocio). */
  walletUuid: string;
  /** Moneda origen preseleccionada (la que el operador clickeó). */
  fromCurrency: string;
  /** Saldo disponible en la moneda origen, para validar contra el monto. */
  fromBalance: number;
  /** Monedas disponibles en la wallet (para los selectores). */
  currencies: string[];
}

const CRYPTO = new Set(['usdt', 'usdc', 'btc']);

@Component({
  selector: 'app-convert-currency-dialog',
  templateUrl: './convert-currency-dialog.component.html',
  styleUrls: ['./convert-currency-dialog.component.sass'],
})
export class ConvertCurrencyDialogComponent implements OnInit, OnDestroy {
  from = '';
  to = '';
  amount: number | null = null;

  currencyOptions: string[] = [];

  /** Order idempotente: se genera UNA vez al abrir y se reusa en cada submit. */
  readonly order: string;

  plan: VitaConversionPlan | null = null;
  quoting = false;
  quoteError: string | null = null;

  submitting = false;
  result: VitaConversionResult | null = null;
  submitError: string | null = null;

  private quote$ = new Subject<void>();
  private sub = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<ConvertCurrencyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConvertCurrencyData,
    private vita: VitaWalletService,
  ) {
    this.from = (data.fromCurrency || '').toLowerCase();
    this.order = this.buildOrder();

    // Catálogo de monedas: las de la wallet + el set estándar de Vita.
    const base = (data.currencies || []).map((c) => c.toLowerCase());
    const standard = [
      'usd', 'clp', 'cop', 'mxn', 'ars', 'eur', 'brl',
      'usdc', 'usdt', 'btc',
    ];
    this.currencyOptions = Array.from(new Set([...base, ...standard])).sort();

    // Destino default coherente con la regla fiat↔cripto:
    // si origen es fiat → USDT; si origen es cripto → USD.
    this.to = this.isCrypto(this.from) ? 'usd' : 'usdt';
  }

  ngOnInit(): void {
    this.sub.add(
      this.quote$.pipe(debounceTime(350)).subscribe(() => this.runQuote()),
    );
    this.loadConvertibleCurrencies();
    this.requestQuote();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private buildOrder(): string {
    const rnd = Math.random().toString(36).slice(2, 8).toUpperCase();
    const ts = Date.now().toString(36).toUpperCase();
    return `CONV-${ts}-${rnd}`;
  }

  /**
   * Refina el catálogo a las monedas que Vita realmente cotiza en
   * `crypto_prices`. Evita que el operador elija una moneda sin tasa.
   * Si la llamada falla, queda el catálogo base del constructor.
   */
  private loadConvertibleCurrencies(): void {
    this.sub.add(
      this.vita.getCryptoPrices().subscribe({
        next: (res) => {
          const keys = Object.keys(res?.prices || {}).map((k) => k.toLowerCase());
          if (keys.length) {
            this.currencyOptions = Array.from(new Set(keys)).sort();
            if (!this.currencyOptions.includes(this.from)) {
              this.from = this.currencyOptions[0];
            }
            this.normalizeDestination();
            this.requestQuote();
          }
        },
        error: () => {
          /* fallback: catálogo base ya seteado en el constructor */
        },
      }),
    );
  }

  isCrypto(c: string): boolean {
    return CRYPTO.has((c || '').toLowerCase());
  }

  get sameCurrency(): boolean {
    return !!this.from && this.from === this.to;
  }

  /** Vita no convierte fiat→fiat directo. */
  get fiatToFiat(): boolean {
    return !!this.from && !!this.to && !this.isCrypto(this.from) && !this.isCrypto(this.to);
  }

  /** Destinos válidos para el origen actual (regla fiat↔cripto / cripto↔cualquiera). */
  get validDestinations(): string[] {
    return this.currencyOptions.filter((c) => {
      if (c === this.from) return false;
      // si origen es fiat, destino debe ser cripto; si origen es cripto, cualquiera.
      return this.isCrypto(this.from) ? true : this.isCrypto(c);
    });
  }

  /** Si el destino actual quedó inválido, lo reubica al primer válido. */
  private normalizeDestination(): void {
    if (this.sameCurrency || this.fiatToFiat || !this.currencyOptions.includes(this.to)) {
      this.to = this.validDestinations[0] || this.to;
    }
  }

  get exceedsBalance(): boolean {
    if (this.amount == null) return false;
    return this.amount > (this.data.fromBalance || 0);
  }

  get canQuote(): boolean {
    return (
      !!this.from &&
      !!this.to &&
      !this.sameCurrency &&
      !this.fiatToFiat &&
      this.amount != null &&
      this.amount > 0
    );
  }

  get canConvert(): boolean {
    return (
      this.canQuote &&
      !!this.plan &&
      !this.quoting &&
      !this.submitting &&
      !this.result
    );
  }

  // ─── Quote ───

  requestQuote(): void {
    this.plan = null;
    this.quoteError = null;
    if (!this.canQuote) return;
    this.quoting = true;
    this.quote$.next();
  }

  private runQuote(): void {
    if (!this.canQuote) {
      this.quoting = false;
      return;
    }
    this.sub.add(
      this.vita
        .quoteConversion({
          currency_sent: this.from,
          currency_received: this.to,
          amount_sent: this.amount as number,
        })
        .subscribe({
          next: (plan) => {
            this.plan = plan;
            this.quoting = false;
          },
          error: (err) => {
            this.quoting = false;
            if (err?.transient) return;
            this.quoteError =
              typeof err === 'string' ? err : 'No se pudo cotizar la conversión';
          },
        }),
    );
  }

  onFromChange(): void {
    this.normalizeDestination();
    this.requestQuote();
  }

  onToChange(): void {
    this.requestQuote();
  }

  onAmountChange(): void {
    this.requestQuote();
  }

  // ─── Convert ───

  confirm(): void {
    if (!this.canConvert) return;
    this.submitting = true;
    this.submitError = null;
    this.sub.add(
      this.vita
        .convertCurrency({
          currency_sent: this.from,
          currency_received: this.to,
          amount_sent: this.amount as number,
          order: this.order,
        })
        .subscribe({
          next: (res) => {
            this.submitting = false;
            this.result = res;
          },
          error: (err) => {
            this.submitting = false;
            this.submitError =
              typeof err === 'string' ? err : 'Error ejecutando la conversión';
          },
        }),
    );
  }

  /** Cierra devolviendo true si hubo movimiento (para refrescar saldos). */
  close(): void {
    this.dialogRef.close(!!this.result);
  }

  trackByStr = (_: number, s: string) => s;
}
